function formatMoney(cents: number): string {
  const moneyFormat: string = (window as any).Shopify?.moneyFormat || '${{amount}}';
  const amount = (cents / 100).toFixed(2);
  const amountNoDecimals = Math.round(cents / 100).toString();
  return moneyFormat
    .replace('{{amount}}', amount)
    .replace('{{amount_no_decimals}}', amountNoDecimals)
    .replace('{{amount_with_comma_separator}}', amount.replace('.', ','));
}

class QuickViewModal extends HTMLElement {
  private selectedOptions: Record<number, string> = {};
  private currentProduct: any = null;
  private currentVariant: any = null;
  private isOpening = false;
  private triggersBound = false;

  private get shopifyRoot(): string {
    return (window as any).Shopify?.routes?.root ?? '/';
  }

  private get cartAddUrl(): string {
    return (window as any).Theme?.routes?.cart_add_url ?? `${this.shopifyRoot}cart/add.js`;
  }

  connectedCallback(): void {
    this.bindEvents();
    this.bindGlobalTriggers();
  }

  private bindEvents(): void {
    this.querySelector('.quick-view__backdrop')?.addEventListener('click', () => this.close());
    this.querySelector('.quick-view__close')?.addEventListener('click', () => this.close());

    document.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
    });

    const form = this.querySelector('.quick-view__form') as HTMLFormElement;
    form?.addEventListener('submit', (e: Event) => {
      e.preventDefault();
      this.handleAddToCart();
    });
  }

  private bindGlobalTriggers(): void {
    if (this.triggersBound) return;
    this.triggersBound = true;
    document.addEventListener('click', (e: MouseEvent) => {
      const btn = (e.target as HTMLElement).closest('[data-quick-view-handle]') as HTMLElement | null;
      if (btn) {
        e.preventDefault();
        const handle = btn.getAttribute('data-quick-view-handle');
        if (handle) this.open(handle, btn as HTMLButtonElement);
      }
    });
  }

  async open(handle: string, triggerBtn?: HTMLButtonElement): Promise<void> {
    if (this.isOpening) return;
    this.isOpening = true;
    try {
      const res = await fetch(`${this.shopifyRoot}products/${handle}.js`);
      if (!res.ok) throw new Error(`Product fetch failed: ${res.status} ${res.url}`);
      this.currentProduct = await res.json();

      // Single variant: skip the modal and add to cart directly
      if (this.currentProduct.variants?.length === 1) {
        const variant = this.currentProduct.variants[0];
        if (variant.available) {
          await this.addToCartDirect(variant.id, triggerBtn);
        }
        return;
      }

      this.selectedOptions = {};

      const firstVariant = this.currentProduct.variants?.[0];
      if (firstVariant) {
        [0, 1, 2].forEach(i => {
          const val = firstVariant[`option${i + 1}`];
          if (val != null) this.selectedOptions[i] = val;
        });
        this.currentVariant = firstVariant;
      }

      this.render();
      this.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      (this.querySelector('.quick-view__close') as HTMLElement)?.focus();
    } catch (err) {
      console.error('[QuickView] open failed:', err);
    } finally {
      this.isOpening = false;
    }
  }

  private async addToCartDirect(variantId: number, btn?: HTMLButtonElement): Promise<void> {
    const originalText = btn?.textContent ?? 'Quick View';
    if (btn) {
      btn.disabled = true;
      btn.textContent = 'Adding...';
    }

    try {
      const res = await fetch(this.cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      });

      if (res.ok) {
        if (btn) btn.textContent = 'Added!';
        if (typeof (window as any).toggleMiniCart === 'function') (window as any).toggleMiniCart('open');
        if (typeof (window as any).getCart === 'function') (window as any).getCart();
        document.dispatchEvent(new CustomEvent('cart:updated'));
        setTimeout(() => {
          if (btn) {
            btn.disabled = false;
            btn.textContent = originalText;
          }
        }, 1000);
      } else {
        if (btn) {
          btn.disabled = false;
          btn.textContent = originalText;
        }
      }
    } catch {
      if (btn) {
        btn.disabled = false;
        btn.textContent = originalText;
      }
    }
  }

  close(): void {
    this.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  private render(): void {
    const p = this.currentProduct;
    if (!p) return;

    const imageEl = this.querySelector('.quick-view__image') as HTMLImageElement;
    const vendorEl = this.querySelector('.quick-view__vendor') as HTMLElement;
    const titleEl = this.querySelector('.quick-view__title') as HTMLElement;
    const priceWrap = this.querySelector('.quick-view__price-wrap') as HTMLElement;
    const variantTitleEl = this.querySelector('.quick-view__variant-title') as HTMLElement;
    const skuEl = this.querySelector('.quick-view__sku') as HTMLElement;
    const optionsEl = this.querySelector('.quick-view__options') as HTMLElement;
    const variantInput = this.querySelector('.quick-view__variant-id') as HTMLInputElement;
    const atcBtn = this.querySelector('.quick-view__atc') as HTMLButtonElement;
    const pdpLink = this.querySelector('.quick-view__pdp-link') as HTMLAnchorElement;

    const imgSrc = this.currentVariant?.featured_image?.src || p.featured_image;
    if (imgSrc) {
      imageEl.src = imgSrc.startsWith('//') ? `https:${imgSrc}` : imgSrc;
      imageEl.alt = p.title;
    }

    vendorEl.textContent = p.vendor || '';
    vendorEl.style.display = p.vendor ? '' : 'none';
    titleEl.textContent = p.title;
    pdpLink.href = p.url;
    pdpLink.textContent = 'View full details →';

    pdpLink.textContent = 'View full details ->';

    optionsEl.innerHTML = '';
    if (p.options) {
      p.options.forEach((option: string | { name: string }, idx: number) => {
        const optionName = typeof option === 'string' ? option : option.name;

        const values: string[] = [
          ...new Set<string>(
            p.variants
              .map((v: any) => v[`option${idx + 1}`])
              .filter((val: any) => Boolean(val) && val !== 'Default Title')
          )
        ];
        if (values.length === 0) return;
        if (values.length === 1 && values[0] === 'Default Title') return;

        const group = document.createElement('div');
        group.className = 'quick-view__option-group';
        group.innerHTML = `<p class="quick-view__option-label">${optionName}</p>`;

        const valuesWrap = document.createElement('div');
        valuesWrap.className = 'quick-view__option-values';

        values.forEach(val => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.textContent = val;
          btn.className = 'quick-view__option-btn';
          btn.dataset.index = String(idx);
          btn.dataset.value = val;
          if (this.selectedOptions[idx] === val) btn.classList.add('is-selected');
          btn.addEventListener('click', () => this.selectOption(idx, val));
          valuesWrap.appendChild(btn);
        });

        group.appendChild(valuesWrap);
        optionsEl.appendChild(group);
      });
    }

    this.updatePriceAndButton(priceWrap, variantTitleEl, skuEl, atcBtn, variantInput);
  }

  private selectOption(idx: number, value: string): void {
    this.selectedOptions[idx] = value;

    this.querySelectorAll('.quick-view__option-btn').forEach(el => {
      const btn = el as HTMLButtonElement;
      if (parseInt(btn.dataset.index!) === idx) {
        btn.classList.toggle('is-selected', btn.dataset.value === value);
      }
    });

    this.currentVariant = this.findVariant();

    const imgSrc = this.currentVariant?.featured_image?.src;
    if (imgSrc) {
      const img = this.querySelector('.quick-view__image') as HTMLImageElement;
      img.src = imgSrc.startsWith('//') ? `https:${imgSrc}` : imgSrc;
    }

    const priceWrap = this.querySelector('.quick-view__price-wrap') as HTMLElement;
    const atcBtn = this.querySelector('.quick-view__atc') as HTMLButtonElement;
    const variantInput = this.querySelector('.quick-view__variant-id') as HTMLInputElement;
    const variantTitleEl = this.querySelector('.quick-view__variant-title') as HTMLElement;
    const skuEl = this.querySelector('.quick-view__sku') as HTMLElement;
    this.updatePriceAndButton(priceWrap, variantTitleEl, skuEl, atcBtn, variantInput);
  }

  private getVariantDisplayTitle(variant: any): string {
    const rawTitle = typeof variant?.title === 'string' ? variant.title : '';
    if (rawTitle && rawTitle !== 'Default Title') return rawTitle;

    const optionValues = Object.keys(this.selectedOptions)
      .map(i => this.selectedOptions[parseInt(i, 10)])
      .filter(v => Boolean(v) && v !== 'Default Title');

    return optionValues.length > 0 ? optionValues.join(' / ') : '';
  }

  private findVariant(): any {
    return this.currentProduct?.variants.find((v: any) =>
      Object.entries(this.selectedOptions).every(
        ([i, val]) => v[`option${parseInt(i) + 1}`] === val
      )
    ) ?? null;
  }

  private updatePriceAndButton(
    priceWrap: HTMLElement,
    variantTitleEl: HTMLElement,
    skuEl: HTMLElement,
    atcBtn: HTMLButtonElement,
    variantInput: HTMLInputElement
  ): void {
    const v = this.currentVariant;

    if (!v) {
      priceWrap.innerHTML = '';
      variantTitleEl.textContent = '';
      skuEl.textContent = '';
      atcBtn.disabled = true;
      atcBtn.textContent = 'Unavailable';
      variantInput.value = '';
      return;
    }

    variantInput.value = String(v.id);
    variantTitleEl.textContent = this.getVariantDisplayTitle(v);
    skuEl.textContent = v.sku ? `SKU: ${v.sku}` : '';

    const price = formatMoney(v.price);
    const hasCompare = v.compare_at_price && v.compare_at_price > v.price;

    priceWrap.innerHTML = hasCompare
      ? `<span class="quick-view__price-sale">${price}</span><s class="quick-view__price-compare">${formatMoney(v.compare_at_price)}</s>`
      : `<span class="quick-view__price-regular">${price}</span>`;

    atcBtn.disabled = !v.available;
    atcBtn.textContent = v.available ? 'Add to Cart' : 'Sold Out';
  }

  private async handleAddToCart(): Promise<void> {
    const variantInput = this.querySelector('.quick-view__variant-id') as HTMLInputElement;
    const atcBtn = this.querySelector('.quick-view__atc') as HTMLButtonElement;
    const variantId = variantInput?.value;
    if (!variantId) return;

    atcBtn.disabled = true;
    atcBtn.textContent = 'Adding...';

    try {
      const res = await fetch(this.cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 }),
      });

      if (res.ok) {
        atcBtn.textContent = 'Added!';
        this.close();
        if (typeof (window as any).toggleMiniCart === 'function') (window as any).toggleMiniCart('open');
        if (typeof (window as any).getCart === 'function') (window as any).getCart();
        document.dispatchEvent(new CustomEvent('cart:updated'));
        setTimeout(() => {
          atcBtn.disabled = false;
          atcBtn.textContent = 'Add to Cart';
        }, 1000);
      } else {
        const errData = await res.json().catch(() => ({}));
        console.error('[QuickView] ATC error:', errData);
        atcBtn.disabled = false;
        atcBtn.textContent = 'Add to Cart';
      }
    } catch {
      atcBtn.disabled = false;
      atcBtn.textContent = 'Add to Cart';
    }
  }
}

if (!customElements.get('quick-view-modal')) {
  customElements.define('quick-view-modal', QuickViewModal);
}

export default QuickViewModal;
